import React, { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import FloatingDDRecords from '../components/FloatingDDRecords';
import FloatingDDByEA from '../components/FloatingDDByEA';
import DailyPerformanceCalendar from '../components/dashboard/DailyPerformanceCalendar';
import AccountDrawdownHistory from '../components/dashboard/AccountDrawdownHistory';
import CumulativePerformance from '../components/dashboard/CumulativePerformance';
import SymbolPerformanceBreakdown from '../components/dashboard/SymbolPerformanceBreakdown';
import AccountEquityCurve from '../components/dashboard/AccountEquityCurve';
import AIHub from '../components/AIHub';
import CountUp from '../components/CountUp';
import GetStartedChecklist from '../components/GetStartedChecklist';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { ChevronDown, ChevronRight, Eye, EyeOff, Pause, Play, Wallet, Calendar, Globe, TrendingUp, TrendingDown, Bot, BarChart3, Target, Cpu, Wifi, AlertTriangle, Database, Clock } from 'lucide-react';
import { apiGet } from '../lib/apiClient';

const authFetch = (url) => {
  return apiGet(url)
    .catch(() => null);
};

export default function Dashboard() {
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'real' | 'demo'
  const [summary, setSummary] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [eaStats, setEaStats] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [symbolStats, setSymbolStats] = useState(null);
  const [hourData, setHourData] = useState(null);
  const [monthlyPnl, setMonthlyPnl] = useState(null);
  const [appSettings, setAppSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataWarning, setDataWarning] = useState('');
  const [hideValues, setHideValues] = useState(false);
  const [paused, setPaused] = useState(false);
  const [aiHubOpen, setAiHubOpen] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const { lastMessage } = useWebSocket();

  // Fetch app settings (withdrawal %, tax rate, refresh interval)
  useEffect(() => {
    authFetch('/api/settings').then(setAppSettings).catch(() => {});
    // Fetch ALL accounts once for the dropdown (never filtered)
    authFetch('/api/accounts').then(accts => { if (Array.isArray(accts)) setAllAccounts(accts); }).catch(() => {});
  }, []);

  // Get refresh interval from settings (default 60s)
  const refreshInterval = appSettings?.refresh_interval || 60;

  // Reset countdown when settings change
  useEffect(() => {
    if (appSettings?.refresh_interval) {
      setRefreshCountdown(appSettings.refresh_interval);
    }
  }, [appSettings?.refresh_interval]);

  // Collapsible sections
  const [collapsed, setCollapsed] = useState({});
  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const fetchAll = useCallback(() => {
    const aid = selectedAccount !== 'all' ? selectedAccount : '';
    const tf = typeFilter !== 'all' ? typeFilter : '';

    // Build query strings
    const summaryQ = aid ? `?account_id=${aid}` : (tf ? `?type_filter=${tf}` : '');
    const eaQ = aid ? `?account_id=${aid}` : (tf ? `?type_filter=${tf}` : '');
    const reportAcct = aid ? `&account=${aid}` : (tf ? `&type_filter=${tf}` : '');
    const monthlyAcct = aid || 'all';

    const requests = [
      authFetch(`/api/dashboard/summary${summaryQ}`),
      authFetch(`/api/ea-stats${eaQ}`),
      authFetch(`/api/reports/performance?period=all${reportAcct}`),
      authFetch(`/api/reports/by-symbol?period=all${reportAcct}`),
      authFetch(`/api/reports/by-hour?period=all${reportAcct}`),
      authFetch(`/api/reports/analytics/monthly-pnl?account=${monthlyAcct}${tf ? `&type_filter=${tf}` : ''}`),
    ];
    const setters = [setSummary, setEaStats, setPerformance, setSymbolStats, setHourData, setMonthlyPnl];

    Promise.allSettled(requests).then(results => {
      let successful = 0;
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value !== null) {
          setters[index](result.value);
          successful += 1;
        }
      });
      setDataWarning(successful === requests.length
        ? ''
        : successful > 0
          ? 'Some dashboard sections could not refresh. Showing the latest available data.'
          : 'Dashboard data could not be loaded. Please retry.');
    }).finally(() => setLoading(false));
  }, [selectedAccount, typeFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (lastMessage && !paused) fetchAll(); }, [lastMessage, paused, fetchAll]);

  // Refresh countdown
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) { fetchAll(); return refreshInterval; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [paused, fetchAll, refreshInterval]);

  if (loading && !summary) return <DashboardSkeleton />;
  if (!summary && dataWarning) {
    return <div className="card text-center py-12"><AlertTriangle size={28} className="text-amber-400 mx-auto mb-3" /><p className="text-gray-300 mb-4">{dataWarning}</p><button onClick={fetchAll} className="px-4 py-2 rounded-lg bg-accent-green text-dark-bg text-sm font-bold">Retry</button></div>;
  }

  const s = summary || {};
  const pnl = s.period_pnl || {};
  const accounts = s.accounts || [];
  const stats = performance?.stats || {};
  const syncHealth = s.sync_health || {};
  const completeness = s.data_completeness || {};
  const totalProfit = s.total_closed_profit || stats.net_pnl || 0;
  const totalTrades = (stats.total_trades || 0) + (s.open_positions || 0);
  const maxDD = stats.max_drawdown || 0;
  const maxDDPct = s.total_balance > 0 ? ((maxDD / s.total_balance) * 100).toFixed(1) : '0.0';

  const mask = (val) => hideValues ? '••••••' : val;

  return (
    <div className="space-y-4 animate-stagger">
      {dataWarning && <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"><AlertTriangle size={14} />{dataWarning}</div>}
      {/* Onboarding checklist (hides once first trade arrives or dismissed) */}
      <AnnouncementsBanner />
      <GetStartedChecklist hasAccounts={allAccounts.length > 0} hasTrades={totalTrades > 0} />

      {/* ═══ HEADER BAR ═══ */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
          <select
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
            className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-md px-3 py-1.5 text-sm text-[var(--text-primary)] min-w-[180px]"
          >
            <option value="all">All Accounts ({(typeFilter === 'all' ? allAccounts : allAccounts.filter(a => typeFilter === 'real' ? !a.is_demo : a.is_demo)).length})</option>
            {allAccounts.filter(a => typeFilter === 'all' ? true : typeFilter === 'real' ? !a.is_demo : a.is_demo).map(a => (
              <option key={a.id} value={a.id}>{a.broker} #{a.account_number} {a.is_demo ? '[DEMO]' : '[REAL]'}</option>
            ))}
          </select>
          {/* REAL / DEMO filter */}
          <div className="flex rounded-md overflow-hidden border border-[var(--border-color)]">
            <button onClick={() => { setTypeFilter('all'); setSelectedAccount('all'); }} className={`px-3 py-1.5 text-xs font-semibold ${typeFilter === 'all' ? 'bg-[#378ADD] text-white' : 'bg-[var(--bg-main)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>All</button>
            <button onClick={() => { setTypeFilter('real'); setSelectedAccount('all'); }} className={`px-3 py-1.5 text-xs font-semibold ${typeFilter === 'real' ? 'bg-[#00ff88] text-[var(--bg-main)]' : 'bg-[var(--bg-main)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>Real</button>
            <button onClick={() => { setTypeFilter('demo'); setSelectedAccount('all'); }} className={`px-3 py-1.5 text-xs font-semibold ${typeFilter === 'demo' ? 'bg-[#f59e0b] text-[var(--bg-main)]' : 'bg-[var(--bg-main)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>Demo</button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setHideValues(!hideValues)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-[var(--border-color)] hover:bg-[var(--border-color)] text-[var(--text-secondary)]"
          >
            {hideValues ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[var(--text-secondary)]">Refresh:</span>
            <span className="text-[#00ff88] font-mono w-8">{refreshCountdown}s</span>
            <button
              onClick={() => setPaused(!paused)}
              className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium ${
                paused ? 'bg-[#00ff88] text-[var(--bg-main)]' : 'bg-[#378ADD] text-white'
              }`}
            >
              {paused ? <><Play size={10} /> Resume</> : <><Pause size={10} /> Pause</>}
            </button>
          </div>
          <span className="text-[var(--text-secondary)] text-xs hidden md:inline">Updated: {new Date().toLocaleString('en-US', { hour12: false }).replace(',', '')}</span>
          <button
            onClick={() => setAiHubOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#a855f7] to-[#3b82f6] text-white hover:opacity-90 transition"
          >
            <Cpu size={12} /> AI Hub
          </button>
        </div>
      </div>

      {/* ═══ HERO STATS (asymmetric — big balance left, metrics right) ═══ */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-4 lg:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 gradient-border glass-card">
        {/* Left: Balance hero */}
        <div className="lg:col-span-5 flex flex-col justify-center">
          <p className="text-[var(--text-secondary)] text-[11px] uppercase tracking-widest font-display mb-1">Total Balance</p>
          <p className="text-[#00ff88] text-2xl lg:text-4xl font-bold font-mono tracking-tight leading-none mb-3">
            {hideValues ? '••••••' : <CountUp value={s.total_balance || 0} prefix="$" decimals={2} />}
          </p>
          <div className="flex items-center gap-3 lg:gap-4 mt-1 flex-wrap">
            <div>
              <p className="text-[var(--text-secondary)] text-[9px] uppercase">Equity</p>
              <p className="text-[#a855f7] text-sm lg:text-base font-bold font-mono">${mask(fmt(s.total_equity))}</p>
            </div>
            <div className="w-px h-8 bg-[var(--border-color)]" />
            <div>
              <p className="text-[var(--text-secondary)] text-[9px] uppercase">Floating P/L</p>
              <p className={`text-sm lg:text-base font-bold font-mono ${(s.floating_pnl || 0) >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
                ${mask(fmt(s.floating_pnl))}
              </p>
            </div>
            <div className="w-px h-8 bg-[var(--border-color)]" />
            <div>
              <p className="text-[var(--text-secondary)] text-[9px] uppercase">Open</p>
              <p className="text-[var(--text-primary)] text-sm lg:text-base font-bold font-mono">{mask(s.open_positions || 0)}</p>
            </div>
          </div>
        </div>

        {/* Right: Metric cards grid */}
        <div className="lg:col-span-7 grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3">
          <MetricTile label="CLOSED P/L" value={`$${mask(fmt(stats.net_pnl))}`} color={(stats.net_pnl || 0) >= 0 ? '#00ff88' : '#ff4757'} />
          <MetricTile label="TOTAL PROFIT" value={`$${mask(fmt(totalProfit))}`} color={totalProfit >= 0 ? '#00ff88' : '#ff4757'} />
          <MetricTile label="TOTAL TRADES" value={mask(totalTrades)} color="var(--text-primary, #e6edf3)" />
          <MetricTile label="WIN RATE" value={`${(stats.win_rate || 0).toFixed(0)}%`} color="#22d3ee" />
          <MetricTile label="PROFIT FACTOR" value={(stats.profit_factor || 0).toFixed(2)} color="#a855f7" />
          <MetricTile label="MAX DRAWDOWN" value={`$${mask(fmt(maxDD))}`} sub={`${maxDDPct}%`} color="#f59e0b" />
        </div>
      </div>

      {/* ═══ PERIOD P&L SUMMARY ═══ */}
      <Section title="Period P&L Summary" id="period-pnl" collapsed={collapsed} toggle={toggle}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PeriodCard label="TODAY'S P/L" value={pnl.today} compareLabel="vs yesterday" />
          <PeriodCard label="THIS WEEK'S P/L" value={pnl.week} compareLabel="vs last week" />
          <PeriodCard label="THIS MONTH'S P/L" value={pnl.month} compareLabel="vs last month" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <PeriodCard label="TODAY'S TRADES" value={pnl.today_trades || 0} isCount />
          <PeriodCard label="THIS WEEK'S TRADES" value={pnl.week_trades || 0} isCount />
          <PeriodCard label="THIS MONTH'S TRADES" value={pnl.month_trades || 0} isCount />
        </div>
      </Section>

      {/* ═══ WITHDRAWAL CALCULATOR ═══ */}
      <Section title="Withdrawal Calculator" id="take-home" collapsed={collapsed} toggle={toggle}>
        <TakeHomeCalculator profit={totalProfit} periodPnl={pnl} balance={s.total_balance} settings={appSettings} />
      </Section>

      {/* ═══ DRAWDOWN ANALYSIS ═══ */}
      <Section title="Drawdown Analysis" id="drawdown" collapsed={collapsed} toggle={toggle}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DrawdownCard label="MAX DRAWDOWN" value={stats.max_drawdown} balance={s.total_balance} color="text-[#f59e0b]" />
          <DrawdownCard label="AVG DRAWDOWN" value={stats.avg_drawdown} balance={s.total_balance} color="text-[var(--text-secondary)]" />
          <DrawdownCard label="CURRENT DRAWDOWN" value={stats.current_drawdown} balance={s.total_balance} color="text-[#f59e0b]" />
        </div>
      </Section>

      {/* ═══ FLOATING DD RECORDS ═══ */}
      <FloatingDDRecords accountId={selectedAccount} typeFilter={typeFilter} />

      {/* ═══ MONTHLY PERFORMANCE HEATMAP ═══ */}
      <Section title="Monthly Performance Heatmap" id="monthly-heatmap" collapsed={collapsed} toggle={toggle}>
        <MonthlyHeatmap data={monthlyPnl} />
      </Section>

      {/* ═══ DAILY PERFORMANCE CALENDAR ═══ */}
      <Section title="📆 Daily Performance Calendar" id="daily-calendar" collapsed={collapsed} toggle={toggle}>
        <DailyPerformanceCalendar accountId={selectedAccount} typeFilter={typeFilter} />
      </Section>

      {/* ═══ SESSION PERFORMANCE ═══ */}
      <Section title="Session Performance" id="sessions" collapsed={collapsed} toggle={toggle}>
        <SessionCards data={hourData} />
      </Section>

      {/* ═══ ACCOUNT EQUITY CURVE ═══ */}
      <Section title="Account Equity Curve" id="equity-curve" collapsed={collapsed} toggle={toggle}>
        <AccountEquityCurve equityCurve={performance?.equity_curve || []} balance={s.total_balance || 0} />
      </Section>

      {/* ═══ ACCOUNT DRAWDOWN HISTORY ═══ */}
      <Section title="Account Drawdown History" id="dd-history" collapsed={collapsed} toggle={toggle}>
        <AccountDrawdownHistory equityCurve={performance?.equity_curve || []} accountId={selectedAccount} typeFilter={typeFilter} balance={s.total_balance || 0} />
      </Section>

      {/* ═══ EA PERFORMANCE COMPARISON ═══ */}
      <Section title="EA Performance Comparison" id="ea-comparison" collapsed={collapsed} toggle={toggle}>
        <EAComparison eaStats={eaStats} />
      </Section>

      {/* ═══ FLOATING DD BY EA ═══ */}
      <FloatingDDByEA accountId={selectedAccount} typeFilter={typeFilter} />

      {/* ═══ CUMULATIVE PERFORMANCE (3 charts side by side) ═══ */}
      <Section title="Cumulative Performance" id="cumulative" collapsed={collapsed} toggle={toggle}>
        <CumulativePerformance equityCurve={performance?.equity_curve || []} />
      </Section>

      {/* ═══ SYMBOL PERFORMANCE BREAKDOWN ═══ */}
      <Section title="Symbol Performance Breakdown" id="symbol-bars" collapsed={collapsed} toggle={toggle}>
        <SymbolPerformanceBreakdown symbolStats={symbolStats} eaStats={eaStats} accountId={selectedAccount} typeFilter={typeFilter} />
      </Section>

      {/* AI Hub Modal */}
      <AIHub isOpen={aiHubOpen} onClose={() => setAiHubOpen(false)} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUB-COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function Section({ title, id, collapsed, toggle, children, headerExtra }) {
  const isCollapsed = collapsed[id];
  return (
    <div className="glass-card">
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer select-none hover:bg-[var(--bg-card-hover)] rounded-t-xl transition-colors"
        onClick={() => toggle(id)}
      >
        <div className="flex items-center gap-2">
          {isCollapsed
            ? <ChevronRight className="w-3.5 h-3.5 text-[var(--text-secondary)]" />
            : <ChevronDown className="w-3.5 h-3.5 text-[var(--text-secondary)]" />}
          <h2 className="text-[var(--text-primary)] text-sm font-semibold font-display">{title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {headerExtra}
          <ChevronDown className="w-3.5 h-3.5 text-[var(--text-secondary)]" />
        </div>
      </div>
      {!isCollapsed && (
        <div className="px-5 pb-5">{children}</div>
      )}
    </div>
  );
}

function MetricTile({ label, value, color, sub }) {
  return (
    <div className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-lg p-3 card-glow cursor-default">
      <p className="text-[var(--text-secondary)] text-[9px] uppercase tracking-wider font-display mb-1.5">{label}</p>
      <p className="font-bold font-mono text-lg leading-none" style={{ color }}>{value}</p>
      {sub && <p className="text-[var(--text-dim)] text-[10px] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}


function SyncHealthPanel({ syncHealth, completeness, accounts }) {
  const status = syncHealth.status || 'stale';
  const statusConfig = {
    live: { label: 'Live', color: '#00ff88', bg: 'rgba(0, 255, 136, 0.12)' },
    partial: { label: 'Partial', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
    stale: { label: 'Stale', color: '#ff4757', bg: 'rgba(255, 71, 87, 0.12)' },
  }[status] || { label: 'Unknown', color: 'var(--text-secondary)', bg: 'var(--bg-main)' };
  const staleList = completeness.accounts_without_sync || [];
  const namedEaCount = Math.max((completeness.ea_count || 0) - (completeness.unnamed_ea_count || 0), 0);

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-4 grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: statusConfig.bg, color: statusConfig.color }}>
          {status === 'live' ? <Wifi size={18} /> : <AlertTriangle size={18} />}
        </div>
        <div>
          <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide">Sync Health</p>
          <p className="text-lg font-bold" style={{ color: statusConfig.color }}>{statusConfig.label}</p>
          <p className="text-[10px] text-[var(--text-dim)]">{syncHealth.synced_accounts || 0}/{completeness.visible_accounts || accounts.length || 0} accounts fresh</p>
        </div>
      </div>
      <HealthMiniStat icon={<Database size={14} />} label="Visible Trades" value={(completeness.total_trades || 0).toLocaleString()} sub={(completeness.closed_trades || 0).toLocaleString() + " closed / " + (completeness.open_trades || 0).toLocaleString() + " open"} />
      <HealthMiniStat icon={<Bot size={14} />} label="EA Mapping" value={namedEaCount + "/" + (completeness.ea_count || 0)} sub={(completeness.unnamed_ea_count || 0) + " need names"} />
      <HealthMiniStat icon={<Calendar size={14} />} label="History Range" value={formatDateShort(completeness.oldest_trade_date) || "-"} sub={completeness.newest_trade_date ? "to " + formatDateShort(completeness.newest_trade_date) : "No closed trades"} />
      <HealthMiniStat icon={<Clock size={14} />} label="Last Upload" value={formatRelativeTime(syncHealth.last_sync)} sub={formatDateTime(syncHealth.last_sync)} />
      <div className="xl:col-span-2 min-w-0">
        <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide mb-2">Needs Attention</p>
        {staleList.length === 0 && (completeness.unnamed_ea_count || 0) === 0 ? (
          <p className="text-[#00ff88] text-xs font-semibold">All clear</p>
        ) : (
          <div className="space-y-1">
            {staleList.slice(0, 2).map(a => (
              <p key={a.id} className="text-[11px] text-[#f59e0b] truncate">#{a.account_number} stale</p>
            ))}
            {staleList.length > 2 && <p className="text-[11px] text-[var(--text-secondary)]">+{staleList.length - 2} more accounts</p>}
            {(completeness.unnamed_ea_count || 0) > 0 && <p className="text-[11px] text-[#f59e0b]">{completeness.unnamed_ea_count} EA names missing</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function HealthMiniStat({ icon, label, value, sub }) {
  return (
    <div className="xl:col-span-2 flex items-start gap-2 min-w-0">
      <div className="mt-0.5 text-[var(--text-secondary)]">{icon}</div>
      <div className="min-w-0">
        <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide">{label}</p>
        <p className="text-[var(--text-primary)] text-sm font-bold truncate">{value}</p>
        <p className="text-[10px] text-[var(--text-dim)] truncate">{sub}</p>
      </div>
    </div>
  );
}

function formatDateShort(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatDateTime(value) {
  if (!value) return 'No sync yet';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatRelativeTime(value) {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (seconds < 60) return seconds + "s ago";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + "m ago";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + "h ago";
  return Math.floor(hours / 24) + "d ago";
}

function IconStat({ icon, iconBg, label, value, valueColor }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-lg ${iconBg}`}>
        {icon}
      </div>
      <div>
        <p className={`text-lg font-bold font-mono ${valueColor}`}>{value}</p>
        <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

function BigStatCard({ label, value, valueColor }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-5">
      <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide mb-2">{label}</p>
      <p className={`text-2xl font-bold font-mono ${valueColor}`}>{value}</p>
    </div>
  );
}

function PeriodCard({ label, value, compareLabel, isCount }) {
  const v = value || 0;
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-5 ui-card">
      <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide mb-2">{label}</p>
      <p className={`text-xl font-bold font-mono mb-2 ${
        isCount ? 'text-[var(--text-primary)]' :
        v > 0 ? 'text-[#00ff88]' : v < 0 ? 'text-[#ff4757]' : 'text-[var(--text-primary)]'
      }`}>
        {isCount
          ? <CountUp value={v} decimals={0} />
          : <CountUp value={v} prefix="$" decimals={2} />}
      </p>
      {compareLabel && (
        <p className="text-[#64748b] text-[11px]">{compareLabel}</p>
      )}
    </div>
  );
}

function TakeHomeCalculator({ profit, periodPnl, balance, settings }) {
  // Use settings from backend (configured in Settings page), with fallbacks
  const withdrawalPctWeekly = settings?.withdrawal_pct_weekly ?? 30;
  const withdrawalPctMonthly = settings?.withdrawal_pct_monthly ?? 30;
  const taxRate = settings?.tax_rate ?? 25;

  const pnl = periodPnl || {};

  // Use real period data from backend
  const thisMonthProfit = pnl.month || 0;
  const thisYearProfit = pnl.year || 0;
  const thisWeekProfit = pnl.week || 0;

  // Weekly/Monthly withdrawal based on ACTUAL period profit
  // Only show withdrawal if profit is positive (can't withdraw losses)
  const weeklyTakeHome = thisWeekProfit > 0 ? thisWeekProfit * (withdrawalPctWeekly / 100) : 0;
  const monthlyTakeHome = thisMonthProfit > 0 ? thisMonthProfit * (withdrawalPctMonthly / 100) : 0;
  const monthlyAfterTax = monthlyTakeHome > 0 ? monthlyTakeHome * (1 - taxRate / 100) : 0;

  // Tax owed on this month's profit
  const taxOwed = thisMonthProfit > 0 ? thisMonthProfit * (taxRate / 100) : 0;

  // Total withdrawals = 0 for now (no withdrawal tracking yet)
  const totalWithdrawals = 0;

  const withdrawalPct = withdrawalPctMonthly; // For display labels

  return (
    <div className="space-y-3">
      {/* Top row - 4 colored cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <GradientCard
          label={`WEEKLY WITHDRAWAL (${withdrawalPctWeekly}%)`}
          value={`$${fmt(weeklyTakeHome)}`}
          subtext="Based on this week's P/L"
          gradient={weeklyTakeHome >= 0 ? "from-[#0d4429] to-[#1a5e3a]" : "from-[#4a1010] to-[#5e1a1a]"}
          textColor={weeklyTakeHome >= 0 ? "text-[#00ff88]" : "text-[#ff4757]"}
        />
        <GradientCard
          label={`MONTHLY WITHDRAWAL (${withdrawalPctMonthly}%)`}
          value={`$${fmt(monthlyTakeHome)}`}
          subtext="Based on this month's P/L"
          gradient={monthlyTakeHome >= 0 ? "from-[#0d4429] to-[#1a5e3a]" : "from-[#4a1010] to-[#5e1a1a]"}
          textColor={monthlyTakeHome >= 0 ? "text-[#00ff88]" : "text-[#ff4757]"}
        />
        <GradientCard
          label={`MONTHLY AFTER TAX (${taxRate}%)`}
          value={`$${fmt(monthlyAfterTax)}`}
          subtext={`Net after ${taxRate}% tax`}
          gradient="from-[#5c3a0d] to-[#7a4f1a]"
          textColor="text-[#f59e0b]"
        />
        <GradientCard
          label="TOTAL WITHDRAWALS"
          value={`$${fmt(totalWithdrawals)}`}
          subtext={totalWithdrawals > 0 ? 'withdrawals' : 'No withdrawals yet'}
          gradient="from-[#3a1654] to-[#4d1f70]"
          textColor="text-[#a855f7]"
        />
      </div>
      {/* Bottom row - 3 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <GradientCard
          label="THIS MONTH P/L"
          value={`$${fmt(thisMonthProfit)}`}
          subtext=""
          gradient="from-[#3a1654] to-[#4d1f70]"
          textColor={thisMonthProfit >= 0 ? 'text-[#a855f7]' : 'text-[#ff4757]'}
        />
        <GradientCard
          label="THIS YEAR P/L"
          value={`$${fmt(thisYearProfit)}`}
          subtext=""
          gradient="from-[#3a1654] to-[#4d1f70]"
          textColor={thisYearProfit >= 0 ? 'text-[#a855f7]' : 'text-[#ff4757]'}
        />
        <GradientCard
          label={`EST. TAX OWED (${taxRate}%)`}
          value={`$${fmt(taxOwed)}`}
          subtext="On this month's profit"
          gradient="from-[#3a1654] to-[#4d1f70]"
          textColor="text-[var(--text-primary)]"
        />
      </div>
    </div>
  );
}

function GradientCard({ label, value, subtext, gradient, textColor }) {
  return (
    <div className={`bg-gradient-to-br ${gradient} border border-[var(--border-color)] rounded-xl p-4 text-center ui-card`}>
      <p className="text-white/60 text-[10px] uppercase tracking-wide mb-2">{label}</p>
      <p className={`text-xl font-bold font-mono ${textColor}`}>{value}</p>
      {subtext && <p className="text-white/40 text-[10px] mt-1">{subtext}</p>}
    </div>
  );
}

function DrawdownCard({ label, value, balance, color }) {
  const v = value || 0;
  const pct = balance > 0 ? ((v / balance) * 100).toFixed(2) : '0.00';
  return (
    <div className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-xl p-5 text-center ui-card">
      <p className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wide mb-2">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>${fmt(v)} ({pct}%)</p>
    </div>
  );
}

function MonthlyHeatmap({ data }) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // Group by year
  const years = {};
  (data || []).forEach(d => {
    const [year, month] = String(d.month || '').replace(/\./g, '-').split('-');
    if (!year || !month) return;
    if (!years[year]) years[year] = {};
    years[year][parseInt(month)] = d.profit;
  });

  // Calculate yearly totals
  const yearKeys = Object.keys(years).sort();

  const cellColor = (profit) => {
    if (profit === undefined) return null;
    if (profit > 0) {
      return profit > 10000 ? '#00ff88' : '#23a96b';
    }
    if (profit < 0) {
      return profit < -1000 ? '#7a3030' : '#5a4040';
    }
    return '#3a3a3a';
  };

  return (
    <div className="bg-gradient-to-br from-[#0d1f3d] to-[#1a2547] rounded-xl p-4 lg:p-6 overflow-x-auto">
      <div className="grid gap-1.5 min-w-[640px]" style={{ gridTemplateColumns: '52px repeat(12, 1fr) 72px' }}>
        {/* Header */}
        <div className="text-[var(--text-secondary)] text-[11px] flex items-center">Year</div>
        {months.map(m => (
          <div key={m} className="text-[var(--text-secondary)] text-[11px] text-center flex items-center justify-center">{m}</div>
        ))}
        <div className="text-[var(--text-secondary)] text-[11px] text-center flex items-center justify-center">Total</div>

        {/* Rows */}
        {yearKeys.map(year => {
          const total = Object.values(years[year]).reduce((a, b) => a + b, 0);
          return (
            <React.Fragment key={year}>
              <div className="text-[var(--text-primary)] text-sm flex items-center font-medium">{year}</div>
              {Array.from({ length: 12 }, (_, i) => {
                const profit = years[year][i + 1];
                const bg = cellColor(profit);
                if (profit === undefined) {
                  return <div key={i} className="h-9 flex items-center justify-center text-[var(--text-dim)] text-xs">—</div>;
                }
                return (
                  <div
                    key={i}
                    className="h-9 rounded-md flex items-center justify-center text-[10px] font-semibold px-0.5"
                    style={{ backgroundColor: bg, color: profit > 0 ? 'var(--bg-main, #0d1117)' : 'var(--text-primary, #e6edf3)' }}
                    title={`${months[i]} ${year}: ${profit >= 0 ? '+' : ''}$${profit.toFixed(0)}`}
                  >
                    {profit >= 0 ? '+' : ''}{profit.toFixed(0)}
                  </div>
                );
              })}
              <div
                className="h-9 rounded-md flex items-center justify-center text-[10px] font-semibold"
                style={{ backgroundColor: total >= 0 ? '#3d3a8a' : '#7a3030', color: 'var(--text-primary, #e6edf3)' }}
              >
                {total >= 0 ? '+' : ''}{total.toFixed(0)}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function DailyCalendar({ performance }) {
  // Build daily PnL map from equity curve
  const dailyMap = {};
  (performance?.daily_data || []).forEach(d => {
    dailyMap[d.date] = d.profit;
  });

  // Get last 4 months
  const today = new Date();
  const months = [];
  for (let i = 3; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    months.push({ year: d.getFullYear(), month: d.getMonth() });
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {months.map(({ year, month }) => (
        <MiniMonthCalendar key={`${year}-${month}`} year={year} month={month} dailyMap={dailyMap} />
      ))}
    </div>
  );
}

function MiniMonthCalendar({ year, month, dailyMap }) {
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  let monthTotal = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    monthTotal += (dailyMap[key] || 0);
  }

  const cellColor = (profit) => {
    if (profit === undefined || profit === 0) return null;
    if (profit > 0) return profit > 500 ? '#00ff88' : '#1a7a4a';
    return profit < -500 ? '#7a3030' : '#5a4040';
  };

  return (
    <div className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-xl p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1 text-[var(--text-primary)] text-xs font-medium">
          <ChevronRight size={12} className="text-[var(--text-secondary)]" />
          {monthNames[month]} {year}
        </div>
        <span className={`text-[10px] font-semibold ${monthTotal >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
          {monthTotal >= 0 ? '+' : ''}${fmt(monthTotal)}
        </span>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-[8px] text-[var(--text-secondary)] mb-1 text-center uppercase">
        {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => <div key={d}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} className="aspect-square" />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const key = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const profit = dailyMap[key];
          const bg = cellColor(profit);

          return (
            <div
              key={day}
              className="aspect-square rounded-sm flex flex-col items-center justify-center text-[8px] relative"
              style={{ backgroundColor: bg || 'var(--bg-card, #161b22)' }}
              title={profit !== undefined ? `${key}: ${profit >= 0 ? '+' : ''}$${profit.toFixed(0)}` : key}
            >
              <span className="text-[var(--text-primary)]/80 leading-none">{day}</span>
              {profit !== undefined && profit !== 0 && (
                <span className={`text-[6px] leading-none mt-0.5 ${profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
                  {profit >= 0 ? '+' : ''}{Math.round(profit)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SessionCards({ data }) {
  const sessions = [
    { code: 'AU', name: 'Sydney', hours: [22,23,0,1,2,3,4,5,6], color: '#00ff88', timezone: '22:00 - 07:00 UTC' },
    { code: 'JP', name: 'Tokyo', hours: [0,1,2,3,4,5,6,7,8], color: '#f59e0b', timezone: '00:00 - 09:00 UTC' },
    { code: 'GB', name: 'London', hours: [8,9,10,11,12,13,14,15,16], color: '#378ADD', timezone: '08:00 - 17:00 UTC' },
    { code: 'US', name: 'New York', hours: [13,14,15,16,17,18,19,20,21], color: '#a855f7', timezone: '13:00 - 22:00 UTC' },
  ];

  const hourMap = {};
  (data || []).forEach(h => { hourMap[h.hour] = h; });

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {sessions.map(session => {
        let profit = 0, trades = 0, wins = 0;
        let activeHours = 0;
        session.hours.forEach(h => {
          const d = hourMap[h];
          if (d) { profit += d.profit; trades += d.trades; wins += d.wins; if (d.trades > 0) activeHours++; }
        });
        const winRate = trades > 0 ? (wins / trades * 100).toFixed(0) : 0;

        return (
          <div key={session.name} className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-xl p-4 relative overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: session.color }} />

            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-secondary)] font-semibold">{session.code}</span>
                <span className="text-[var(--text-primary)] text-base font-semibold">{session.name}</span>
              </div>
              <span className={`text-base font-bold ${profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
                {profit >= 0 ? '+' : ''}${fmt(profit)}
              </span>
            </div>
            <p className="text-[var(--text-dim)] text-[10px] mb-3">{session.timezone}</p>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[var(--text-primary)] text-base font-bold">{trades}</p>
                <p className="text-[var(--text-secondary)] text-[9px] uppercase">Trades</p>
              </div>
              <div>
                <p className="text-[var(--text-primary)] text-base font-bold">{activeHours}</p>
                <p className="text-[var(--text-secondary)] text-[9px] uppercase">Active Hrs</p>
              </div>
              <div>
                <p className="text-[#00ff88] text-base font-bold">{winRate}%</p>
                <p className="text-[var(--text-secondary)] text-[9px] uppercase">Win Rate</p>
              </div>
            </div>

            <div className="mt-3 w-full h-0.5 bg-[var(--border-color)] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${winRate}%`, backgroundColor: session.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChartToggleButtons({ activeMode, onModeChange, activePeriod, onPeriodChange, periods = [] }) {
  return (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      {/* $ / % toggle */}
      <div className="flex bg-[var(--border-color)] rounded-md overflow-hidden">
        <button
          onClick={() => onModeChange('$')}
          className={`px-2.5 py-1 text-[10px] font-bold transition ${activeMode === '$' ? 'bg-[#00ff88] text-[var(--bg-main)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >$</button>
        <button
          onClick={() => onModeChange('%')}
          className={`px-2.5 py-1 text-[10px] font-bold transition ${activeMode === '%' ? 'bg-[#00ff88] text-[var(--bg-main)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >%</button>
      </div>
      {/* Period buttons */}
      {periods.length > 0 && (
        <div className="flex bg-[var(--border-color)] rounded-md overflow-hidden ml-1">
          {periods.map(p => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`px-2 py-1 text-[10px] font-medium transition ${activePeriod === p ? 'bg-[#00ff88] text-[var(--bg-main)] font-bold' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function filterDataByPeriod(data, period) {
  if (!data || data.length === 0) return [];
  if (period === 'ALL') return data;

  const now = new Date();
  let cutoffDays = 30;
  switch (period) {
    case '1D': cutoffDays = 1; break;
    case '1W': cutoffDays = 7; break;
    case '1M': cutoffDays = 30; break;
    case '3M': cutoffDays = 90; break;
    case '6M': cutoffDays = 180; break;
    case '1Y': cutoffDays = 365; break;
    default: return data;
  }

  const cutoff = new Date(now.getTime() - cutoffDays * 86400000);
  return data.filter(d => {
    if (!d.date) return true;
    return new Date(d.date) >= cutoff;
  });
}

function EquityCurveSection({ data }) {
  const [mode, setMode] = useState('$');
  const [period, setPeriod] = useState('ALL');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const filteredData = filterDataByPeriod(data, period);

  // For % mode, calculate percentage change from first point
  const chartData = mode === '%' && filteredData.length > 0
    ? filteredData.map(d => ({
        ...d,
        equity_pct: filteredData[0].equity !== 0
          ? ((d.equity - filteredData[0].equity) / Math.abs(filteredData[0].equity) * 100)
          : 0
      }))
    : filteredData;

  const dataKey = mode === '%' ? 'equity_pct' : 'equity';
  const yFormatter = mode === '%'
    ? (v) => `${v.toFixed(0)}%`
    : (v) => `$${(v/1000).toFixed(0)}K`;

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl">
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer select-none" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex items-center gap-2">
          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-[var(--text-secondary)]" /> : <ChevronDown className="w-3.5 h-3.5 text-[var(--text-secondary)]" />}
          <h2 className="text-[var(--text-primary)] text-sm font-medium">📈 Account Equity Curve</h2>
        </div>
        <ChartToggleButtons
          activeMode={mode}
          onModeChange={setMode}
          activePeriod={period}
          onPeriodChange={setPeriod}
          periods={['1W','1M','3M','6M','1Y','ALL']}
        />
      </div>
      {!isCollapsed && (
        <div className="px-5 pb-5">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="eqGradGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00ff88" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#00ff88" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #21262d)" />
              <XAxis dataKey="date" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 10 }} />
              <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 10 }} tickFormatter={yFormatter} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px' }}
                formatter={(v) => [mode === '%' ? `${Number(v).toFixed(2)}%` : `$${Number(v).toFixed(2)}`, mode === '%' ? 'Change' : 'Equity']} />
              <Area type="monotone" dataKey={dataKey} stroke="#00ff88" fill="url(#eqGradGreen)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function DrawdownHistorySection({ data }) {
  const [mode, setMode] = useState('$');
  const [period, setPeriod] = useState('ALL');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const filteredData = filterDataByPeriod(data, period);

  // Calculate drawdown from equity curve
  let peak = 0;
  const ddData = filteredData.map(d => {
    const eq = d.equity || 0;
    if (eq > peak) peak = eq;
    const ddDollar = peak - eq;
    const ddPct = peak > 0 ? (ddDollar / peak) * 100 : 0;
    return { date: d.date, drawdown: -ddDollar, drawdown_pct: -ddPct };
  });

  const dataKey = mode === '%' ? 'drawdown_pct' : 'drawdown';
  const yFormatter = mode === '%'
    ? (v) => `${v.toFixed(1)}%`
    : (v) => `$${(v/1000).toFixed(0)}K`;

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl">
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer select-none" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex items-center gap-2">
          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-[var(--text-secondary)]" /> : <ChevronDown className="w-3.5 h-3.5 text-[var(--text-secondary)]" />}
          <h2 className="text-[var(--text-primary)] text-sm font-medium">⚠️ Account Drawdown History</h2>
          <span className="bg-[#f59e0b] text-[var(--bg-main)] text-[10px] font-bold px-2 py-0.5 rounded">⚡ UNDER CONSTRUCTION</span>
        </div>
        <ChartToggleButtons
          activeMode={mode}
          onModeChange={setMode}
          activePeriod={period}
          onPeriodChange={setPeriod}
          periods={['1D','1W','1M','3M','ALL']}
        />
      </div>
      {!isCollapsed && (
        <div className="px-5 pb-5">
          <p className="text-[var(--text-secondary)] text-[11px] mb-2">Data source: Trade MAE (aggregated)</p>
          {ddData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={ddData}>
                <defs>
                  <linearGradient id="ddGradGray" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#9ca3af" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #21262d)" />
                <XAxis dataKey="date" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 10 }} />
                <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 10 }} tickFormatter={yFormatter} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px' }}
                  formatter={(v) => [mode === '%' ? `${Number(v).toFixed(2)}%` : `$${Number(v).toFixed(2)}`, 'Drawdown']} />
                <Area type="monotone" dataKey={dataKey} stroke="#9ca3af" fill="url(#ddGradGray)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[var(--text-dim)] text-sm">No data yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

function EAComparison({ eaStats }) {
  const [checkedEAs, setCheckedEAs] = useState({});

  // Group EAs by normalized name (same logic as MyEAs page)
  const groupedEAs = React.useMemo(() => {
    if (!eaStats) return [];
    const normalizeN = (name) => {
      if (!name) return '';
      return name.replace(/\[.*?\.(ecn|pc|raw|pro|std|micro|cent|stp)\]/gi, '[SYMBOL]').replace(/\[T\d+\/S\d+\]/gi, '').trim().toLowerCase();
    };
    const groups = {};
    eaStats.forEach(ea => {
      const normName = normalizeN(ea.ea_name);
      let found = null;
      for (const [key, members] of Object.entries(groups)) {
        if (members.some(m => m.magic === ea.magic)) { found = key; break; }
        if (normName && members.some(m => normalizeN(m.ea_name) === normName)) { found = key; break; }
      }
      if (found) { groups[found].push(ea); }
      else { groups[String(ea.magic)] = [ea]; }
    });
    return Object.values(groups).map(members => {
      const name = members.reduce((s, m) => (m.ea_name||'').length < (s||'').length ? m.ea_name : s, members[0].ea_name) || `EA_${members[0].magic}`;
      return {
        magic: members[0].magic,
        ea_name: name,
        net_profit: members.reduce((s, m) => s + (m.net_profit || 0), 0),
        total_trades: members.reduce((s, m) => s + (m.total_trades || 0), 0),
        win_rate: (() => { const w = members.reduce((s,m) => s + (m.winning_trades||0), 0); const t = members.reduce((s,m) => s + (m.total_trades||0), 0); return t > 0 ? parseFloat((w/t*100).toFixed(1)) : 0; })(),
        _members: members,
      };
    });
  }, [eaStats]);

  useEffect(() => {
    if (groupedEAs.length > 0 && Object.keys(checkedEAs).length === 0) {
      const initial = {};
      groupedEAs.forEach(ea => { initial[ea.magic] = true; });
      setCheckedEAs(initial);
    }
  }, [groupedEAs]);

  if (!groupedEAs || groupedEAs.length === 0) return <p className="text-[var(--text-dim)] text-sm">No EA data yet.</p>;

  const filtered = groupedEAs.filter(ea => checkedEAs[ea.magic]);
  const allChecked = groupedEAs.every(ea => checkedEAs[ea.magic]);

  const toggleAll = (state) => {
    const next = {};
    groupedEAs.forEach(ea => { next[ea.magic] = state; });
    setCheckedEAs(next);
  };

  return (
    <div>
      <p className="text-[var(--text-secondary)] text-xs mb-3">Combined across all accounts | Interactive filtering enabled</p>

      {/* Filter Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-[var(--text-primary)] text-sm font-medium">Filter EAs</p>
        <div className="flex gap-2">
          <button onClick={() => toggleAll(true)} className="px-3 py-1 bg-[#00ff88] text-[var(--bg-main)] text-[11px] font-bold rounded">Select All</button>
          <button onClick={() => toggleAll(false)} className="px-3 py-1 bg-[#ff4757] text-white text-[11px] font-bold rounded">Deselect All</button>
        </div>
      </div>

      {/* EA Filter Chips Grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
        {groupedEAs.map((ea, i) => (
          <label
            key={ea.magic}
            className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-xs ${
              checkedEAs[ea.magic]
                ? 'border-[var(--border-color)] bg-[var(--bg-main)]'
                : 'border-[var(--border-color)] bg-[var(--bg-main)]/50 opacity-50'
            }`}
          >
            <div className="flex items-center gap-2 truncate">
              <input
                type="checkbox"
                checked={!!checkedEAs[ea.magic]}
                onChange={() => setCheckedEAs(prev => ({ ...prev, [ea.magic]: !prev[ea.magic] }))}
                className="accent-[#00ff88]"
              />
              <span className="text-[var(--text-primary)] truncate">{ea.ea_name || `Magic ${ea.magic}`}</span>
            </div>
            <span className={`font-semibold whitespace-nowrap ${ea.net_profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
              ${fmt(ea.net_profit)}
            </span>
          </label>
        ))}
      </div>

      {/* Bar Chart */}
      <ResponsiveContainer width="100%" height={Math.max(200, filtered.length * 32)}>
        <BarChart data={filtered} layout="vertical" margin={{ left: 130 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #21262d)" horizontal={false} />
          <XAxis type="number" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 10 }} />
          <YAxis type="category" dataKey="ea_name" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-primary, #e6edf3)', fontSize: 11 }} width={130} />
          <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px' }}
            formatter={v => [`$${Number(v).toFixed(2)}`, 'Net Profit']} />
          <Bar dataKey="net_profit" radius={[0, 4, 4, 0]}>
            {filtered.map((entry, i) => (
              <Cell key={i} fill={entry.net_profit >= 0 ? '#23a96b' : '#ff4757'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CumulativeChart({ title, data, color }) {
  return (
    <div className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-xl p-4">
      <p className="text-[var(--text-primary)] text-sm font-medium mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`cum-${title}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.4} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
          <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', fontSize: 11 }} />
          <Area type="monotone" dataKey="equity" stroke={color} fill={`url(#cum-${title})`} strokeWidth={1.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function SymbolBars({ data }) {
  if (!data || data.length === 0) return <p className="text-[var(--text-dim)] text-sm">No symbol data yet.</p>;

  const maxProfit = Math.max(...data.map(s => Math.abs(s.net_profit)), 1);

  return (
    <div className="space-y-2">
      {data.slice(0, 10).map(sym => {
        const width = (Math.abs(sym.net_profit) / maxProfit) * 100;
        return (
          <div key={sym.symbol} className="flex items-center gap-3">
            <span className="text-[var(--text-primary)] text-sm font-semibold w-[100px] truncate">{sym.symbol}</span>
            <div className="flex-1 h-7 bg-[var(--bg-main)] rounded-md overflow-hidden">
              <div
                className="h-full rounded-md flex items-center"
                style={{
                  width: `${width}%`,
                  background: sym.net_profit >= 0
                    ? 'linear-gradient(90deg, #1a7a4a 0%, #00ff88 100%)'
                    : 'linear-gradient(90deg, #7a1a1a 0%, #ff4757 100%)',
                }}
              />
            </div>
            <span className={`text-sm font-semibold w-[80px] text-right ${sym.net_profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
              {sym.net_profit >= 0 ? '+' : ''}${fmt(sym.net_profit)}
            </span>
            <span className="text-[var(--text-secondary)] text-xs w-[80px] text-right">{sym.total_trades} trades</span>
          </div>
        );
      })}
    </div>
  );
}

function SymbolCardsGrid({ data, eaStats }) {
  if (!data || data.length === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {data.map((sym, i) => (
        <SymbolCard key={sym.symbol} symbol={sym} isTop={i === 0} eaStats={eaStats} />
      ))}
    </div>
  );
}

function SymbolCard({ symbol, isTop, eaStats }) {
  const [expanded, setExpanded] = useState(false);

  // Find EAs that contributed to this symbol (we don't have direct mapping, so show all relevant)
  // In a real impl, this would come from a /api/symbols/{symbol}/eas endpoint
  // For now we'll filter EAs and show ones with profits
  const relatedEAs = (eaStats || []).filter(ea => ea.net_profit !== 0).slice(0, 8);

  return (
    <div className="bg-[var(--bg-main)] border border-[var(--border-color)] rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[var(--text-primary)] text-base font-bold">{symbol.symbol}</span>
          {isTop && (
            <span className="bg-[#f59e0b] text-[var(--bg-main)] text-[9px] font-bold px-2 py-0.5 rounded-full">TOP</span>
          )}
        </div>
        <span className={`text-base font-bold ${symbol.net_profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
          {symbol.net_profit >= 0 ? '+' : ''}${fmt(symbol.net_profit)}
        </span>
      </div>

      {/* Stats row */}
      <div className="bg-[var(--bg-card)] rounded-lg p-3 mb-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[var(--text-primary)] text-base font-bold">{symbol.total_trades}</p>
            <p className="text-[var(--text-secondary)] text-[9px] uppercase">Trades</p>
          </div>
          <div>
            <p className="text-[var(--text-primary)] text-base font-bold">{symbol.wins}</p>
            <p className="text-[var(--text-secondary)] text-[9px] uppercase">Wins</p>
          </div>
          <div>
            <p className="text-[#00ff88] text-base font-bold">{symbol.win_rate}%</p>
            <p className="text-[var(--text-secondary)] text-[9px] uppercase">Win Rate</p>
          </div>
        </div>
      </div>

      {/* Win rate bar */}
      <div className="w-full h-1 bg-[var(--border-color)] rounded-full overflow-hidden mb-3">
        <div className="h-full bg-[#00ff88] rounded-full" style={{ width: `${symbol.win_rate}%` }} />
      </div>

      {/* Expand button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[var(--text-secondary)] text-[11px] hover:text-[var(--text-primary)]"
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronRight size={12} />}
        EA BREAKDOWN ({relatedEAs.length})
      </button>

      {/* EA breakdown list */}
      {expanded && (
        <div className="mt-3 space-y-1.5">
          {relatedEAs.map(ea => (
            <div key={ea.magic} className="flex items-center justify-between text-xs">
              <span className="text-[var(--text-primary)] truncate">{ea.ea_name}</span>
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${ea.net_profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
                  ${fmt(ea.net_profit)}
                </span>
                <span className="text-[var(--text-dim)] text-[10px]">({ea.total_trades})</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══ UTILITIES ═══ */

function fmt(num) {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl h-16 animate-pulse" />
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl h-20 animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl h-24 animate-pulse" />)}
      </div>
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl h-32 animate-pulse" />
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl h-64 animate-pulse" />
    </div>
  );
}
